import { useEffect, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { toast } from "sonner";
import SectionHeading from "@/components/SectionHeading";
import BookingForm from "@/components/BookingForm";
import FAQ from "@/components/FAQ";
import AIAgentCTA from "@/components/AIAgentCTA";
import { api } from "@/lib/api";
import { Loader2, Mail, MapPin, Clock, Bot, Users } from "lucide-react";
import AdvisorInterestForm from "@/components/AdvisorInterestForm";

const NEXT_STEPS = [
  {
    n: "01",
    title: "Book the audit",
    body: "Choose a 15-minute window. You\u2019ll receive a calendar invite and a short pre-call form (~3 minutes) so we can come prepared.",
  },
  {
    n: "02",
    title: "60-minute deep-dive",
    body: "A senior advisor walks through your current setup with you. We map the leak. You leave with a clear sense of the gap.",
  },
  {
    n: "03",
    title: "1-page Growth Diagnosis",
    body: "Within 48 hours: your one-page diagnosis and a written, fixed-fee build proposal. No pressure, no obligation, no slide deck.",
  },
];

const FAQS = [
  {
    q: "Is the audit really free?",
    a: "Yes. The 60-minute audit and one-page Growth Diagnosis are complimentary. We run it because (a) it's the best way for both sides to see if there's a fit, and (b) operators who book it tend to be exactly the kind of business we want to work with.",
  },
  {
    q: "What should I have ready for the call?",
    a: "Nothing rehearsed. We'll ask about your current site, where leads come from, how follow-up works, what your team handles, and what your real numbers are. Spreadsheets and dashboards are a bonus but not required.",
  },
  {
    q: "Is this a sales call in disguise?",
    a: "It's a fit call. About 1 in 3 audits doesn't progress to an engagement, and we tell you straight if that's the read. We'd rather lose the build than take on a client who isn't right.",
  },
  {
    q: "Do you sign NDAs?",
    a: "Yes. Mutual NDA is available before the call if you'd like one in place. Default is that we treat everything you share as confidential whether or not paper is signed.",
  },
];

export default function Contact() {
  const { hash } = useLocation();
  const [tab, setTab] = useState("booking"); // booking | message

  useEffect(() => {
    if (hash === "#message") setTab("message");
    else setTab("booking");
    if (hash) {
      const id = hash.replace("#", "");
      setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
    }
  }, [hash]);

  return (
    <>
      <section className="container-x pt-24 md:pt-32 pb-12" data-testid="contact-hero">
        <div className="overline">Book the Growth Audit</div>
        <h1 className="mt-6 max-w-4xl font-display font-light tracking-tight text-4xl sm:text-5xl lg:text-6xl text-balance">
          60 minutes. One diagnosis. Honest answer.
        </h1>
        <p className="mt-8 max-w-2xl text-zinc-300 leading-relaxed text-lg">
          A senior advisor reviews your current setup, identifies the single
          largest leak in your lead-to-revenue funnel, and tells you whether
          the Toulios Growth System is a fit. Complimentary. No pitch deck.
        </p>

        <div className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-3xl">
          {[
            { icon: Mail, k: "Email", v: "hello@touliosWealth.com" },
            { icon: MapPin, k: "Based", v: "Sydney · Servicing all of Australia" },
            { icon: Clock, k: "Response", v: "Within 1 business day" },
          ].map((m) => (
            <div key={m.k} className="border-l border-gold/30 pl-4">
              <m.icon className="w-4 h-4 text-gold" />
              <div className="mt-3 text-[11px] uppercase tracking-[0.2em] text-zinc-500">{m.k}</div>
              <div className="mt-2 text-sm text-zinc-200">{m.v}</div>
            </div>
          ))}
        </div>

        {/* AI fast-path */}
        <div
          className="mt-16 relative overflow-hidden border border-white/[0.06] bg-gradient-to-br from-[#0d0d0d] to-[#070707] p-8 md:p-10"
          data-testid="contact-ai-fastpath"
        >
          <div aria-hidden className="absolute inset-x-0 top-0 h-px gold-line" />
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-8">
              <div className="overline flex items-center gap-2">
                <Bot className="w-3.5 h-3.5" /> Fastest path · Recommended
              </div>
              <h2 className="mt-5 text-2xl md:text-3xl font-display font-light tracking-tight text-balance">
                Skip the calendar tag. Talk to our AI Strategist now.
              </h2>
              <p className="mt-4 text-zinc-400 leading-relaxed max-w-xl">
                A 5 to 15-minute call with our AI agent captures your goals, context and constraints. A senior consultant at Toulios then prepares your tailored plan and contacts you within 1 to 2 business days.
              </p>
            </div>
            <div className="lg:col-span-4 flex flex-col gap-3 lg:items-end">
              <AIAgentCTA testId="contact-ai-fastpath-cta" />
              <span className="text-[11px] tracking-[0.18em] uppercase text-zinc-500">
                Or use the booking form below
              </span>
              <span className="text-[10px] text-zinc-600 leading-relaxed max-w-[220px] lg:text-right">
                Calls are recorded and analysed by our AI agent. Per our{" "}
                <Link to="/trust" className="text-zinc-400 hover:text-gold underline-offset-2 hover:underline">
                  Trust & Privacy
                </Link>
                .
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* WHAT HAPPENS NEXT */}
      <section className="section-y bg-[#070707] border-y border-white/[0.05]" data-testid="next-steps">
        <div className="container-x">
          <SectionHeading
            eyebrow="What happens next"
            title="From booking to diagnosis in under a week."
            description="Three steps. No friction. No drawn-out pitch process."
          />
          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-px bg-white/[0.05]">
            {NEXT_STEPS.map((s) => (
              <div key={s.n} className="bg-ink p-8 md:p-10" data-testid={`next-step-${s.n}`}>
                <div className="overline">{s.n}</div>
                <h3 className="mt-6 text-xl md:text-2xl font-display font-light tracking-tight">{s.title}</h3>
                <p className="mt-4 text-zinc-400 leading-relaxed text-[15px]">
                  {s.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FORM */}
      <section className="section-y" id="booking">
        <div className="container-x">
          <div className="flex flex-wrap items-center gap-2 border-b border-white/[0.06]">
            {[
              { id: "booking", label: "Book the Growth Audit" },
              { id: "message", label: "General enquiry" },
            ].map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => setTab(t.id)}
                data-testid={`tab-${t.id}`}
                className={`px-5 py-3 text-sm tracking-tight transition-colors border-b-2 -mb-px ${
                  tab === t.id
                    ? "border-gold text-white"
                    : "border-transparent text-zinc-500 hover:text-zinc-300"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          <div className="mt-12">
            {tab === "booking" && (
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
                <div className="lg:col-span-5">
                  <SectionHeading
                    eyebrow="The audit"
                    title="Tell us where you&rsquo;re stuck."
                    description="Three minutes now saves 30 on the call. Real numbers welcome, we use them to come prepared."
                  />
                  <ul className="mt-8 space-y-4 text-sm text-zinc-300 max-w-md">
                    {[
                      "Senior advisor on the call, no SDRs, no qualifying gatekeepers.",
                      "Confidential by default. Mutual NDA on request.",
                      "Honest read on fit, sometimes that read is 'not us'.",
                    ].map((b) => (
                      <li key={b} className="flex items-start gap-3">
                        <span className="mt-2 inline-block w-1 h-1 bg-gold flex-shrink-0" />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="lg:col-span-7">
                  <BookingForm />
                </div>
              </div>
            )}

            {tab === "message" && (
              <div id="message" className="grid grid-cols-1 lg:grid-cols-12 gap-12">
                <div className="lg:col-span-5">
                  <SectionHeading
                    eyebrow="General enquiry"
                    title="Send us a note."
                    description="For partnerships, speaking enquiries, media, or anything else that isn&rsquo;t a growth audit."
                  />
                </div>
                <div className="lg:col-span-7">
                  <MessageForm />
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ADVISORY BOARD INTEREST */}
      <section
        className="section-y border-t border-white/[0.05]"
        data-testid="contact-advisor-section"
        id="advisor"
      >
        <div className="container-x">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-5">
              <div className="overline flex items-center gap-2">
                <Users className="w-3.5 h-3.5" /> Senior operators · Optional
              </div>
              <h2 className="mt-6 font-display font-light tracking-tight text-3xl md:text-4xl lg:text-5xl text-balance leading-[1.05]">
                Want to advise alongside Toulios?
              </h2>
              <p className="mt-6 text-zinc-400 leading-relaxed text-[15px] max-w-md">
                We grow the Advisory Board through inbound interest. If you
                have institutional or operator-side experience and would like
                to advise on client engagements at a quarterly cadence, share
                a short pitch. We review every submission. Identities of
                serving advisors are only disclosed publicly with written
                consent.
              </p>
              <p className="mt-6 text-xs text-zinc-500 leading-relaxed">
                Not a placement service. We&rsquo;re selective. Time
                commitment is light but the bar is high.
              </p>
            </div>
            <div className="lg:col-span-7">
              <div className="border border-white/[0.06] bg-[#0a0a0a] p-6 md:p-8">
                <AdvisorInterestForm />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section-y bg-[#070707] border-t border-white/[0.05]" data-testid="contact-faq-section">
        <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <SectionHeading
              eyebrow="FAQ"
              title="Before you book."
              description="The questions every operator asks before getting on the call."
            />
          </div>
          <div className="lg:col-span-7">
            <FAQ items={FAQS} testIdPrefix="contact-faq" />
          </div>
        </div>
      </section>
    </>
  );
}

function MessageForm() {
  const [form, setForm] = useState({ name: "", email: "", company: "", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Name, email and message are required.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/contact", form);
      toast.success(data.message || "Message received.");
      setDone(true);
    } catch {
      toast.error("Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="surface p-10 text-center" data-testid="message-form-success">
        <h3 className="font-display text-2xl">Message received</h3>
        <p className="mt-3 text-zinc-400">Thank you, we&rsquo;ll respond shortly.</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="surface p-8 md:p-10 space-y-5" data-testid="message-form">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <label className="block">
          <span className="block text-[11px] tracking-[0.18em] uppercase text-zinc-500 mb-2">Name <span className="text-gold">*</span></span>
          <input data-testid="message-name" required className="msg-input" value={form.name} onChange={(e) => update("name", e.target.value)} />
        </label>
        <label className="block">
          <span className="block text-[11px] tracking-[0.18em] uppercase text-zinc-500 mb-2">Email <span className="text-gold">*</span></span>
          <input data-testid="message-email" type="email" required className="msg-input" value={form.email} onChange={(e) => update("email", e.target.value)} />
        </label>
        <label className="block">
          <span className="block text-[11px] tracking-[0.18em] uppercase text-zinc-500 mb-2">Company</span>
          <input data-testid="message-company" className="msg-input" value={form.company} onChange={(e) => update("company", e.target.value)} />
        </label>
        <label className="block">
          <span className="block text-[11px] tracking-[0.18em] uppercase text-zinc-500 mb-2">Subject</span>
          <input data-testid="message-subject" className="msg-input" value={form.subject} onChange={(e) => update("subject", e.target.value)} />
        </label>
      </div>
      <label className="block">
        <span className="block text-[11px] tracking-[0.18em] uppercase text-zinc-500 mb-2">Message <span className="text-gold">*</span></span>
        <textarea data-testid="message-body" required rows={6} className="msg-input resize-y" value={form.message} onChange={(e) => update("message", e.target.value)} />
      </label>
      <div className="flex justify-end">
        <button type="submit" disabled={submitting} className="btn-gold disabled:opacity-60" data-testid="message-submit">
          {submitting ? (<><Loader2 className="w-4 h-4 animate-spin" /> Sending…</>) : "Send message"}
        </button>
      </div>
      <style>{`
        .msg-input { width:100%; background:#0a0a0a; border:1px solid rgba(255,255,255,0.08); color:#fff; font-family:'Manrope',sans-serif; font-size:14px; padding:12px 14px; outline:none; border-radius:0; transition:border-color .2s ease; }
        .msg-input:focus { border-color: rgba(212,175,55,0.55); }
      `}</style>
    </form>
  );
}
